<template>
  <div
    class="flex-grow-1 d-flex flex-column"
    :class="[
      display.mdAndUp.value ? 'bg-neutral25' : 'bg-neutral0',
      { 'page-transitioning': transitioning }
    ]"
  >
    <v-main class="pt-md-16">
      <!-- Desktop back button -->
      <BrandButton
        v-if="display.mdAndUp.value && showBackButton"
        icon
        position="fixed"
        variant="basic"
        color="neutral900"
        class="bg-neutral0"
        border="0"
        size="large"
        @click="onBackButtonClicked"
        style="top: 16px; left: 16px; z-index: 10;"
      >
        <PhArrowLeft class="text-h5" />
      </BrandButton>

      <!-- Desktop menu button -->
      <DropdownHybrid
        v-if="display.mdAndUp.value && hasMenuOptions"
        v-model="menuOpen"
        :sheet-title="currentMenuTitle"
        :menu-props="menuProps"
      >
        <template #activator="{ props: activatorProps }">
          <BrandButton
            icon
            position="fixed"
            variant="basic"
            color="neutral900"
            class="bg-neutral0"
            border="0"
            size="large"
            v-bind="activatorProps"
            style="top: 16px; right: 16px; z-index: 10;"
          >
            <PhDotsThreeVertical class="text-h5" />
          </BrandButton>
        </template>

        <v-list bg-color="neutral25" class="py-3">
          <v-list-item
            v-for="option in currentMenuOptions"
            :key="option.key"
            :class="option.destructive ? 'text-red400' : 'text-neutral900'"
            @click="onMenuSelect(option)"
          >
            <template #prepend>
              <component :is="option.icon" class="text-h5 mr-3" />
            </template>
            <v-list-item-title class="font-weight-medium">
              {{ option.text }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </DropdownHybrid>

      <!-- Mobile header -->
      <header
        v-if="display.smAndDown.value"
        class="mobile-header pt-8 pt-sm-2 px-2 d-flex align-center justify-space-between"
        :class="{ 'bg-neutral0': !headerColor }"
        :style="headerColor ? { backgroundColor: headerColor, '--maxWidth': `${maxWidthDesktop}px` } : { '--maxWidth': `${maxWidthDesktop}px` }"
      >
        <BrandButton
          v-if="showBackButton"
          icon
          variant="basic"
          color="neutral900"
          :class="headerColor ? '' : 'bg-neutral0'"
          border="0"
          @click="onBackButtonClicked"
        >
          <PhArrowLeft class="text-h5" />
        </BrandButton>
        <div v-else />

        <!-- Mobile menu button -->
        <DropdownHybrid
          v-if="hasMenuOptions"
          v-model="menuOpen"
          :sheet-title="currentMenuTitle"
          :menu-props="menuProps"
        >
          <template #activator="{ props: activatorProps }">
            <BrandButton
              icon
              variant="basic"
              color="neutral900"
              :class="headerColor ? '' : 'bg-neutral0'"
              border="0"
              v-bind="activatorProps"
            >
              <PhDotsThreeVertical class="text-h5" />
            </BrandButton>
          </template>

          <v-list bg-color="neutral25" class="py-3">
            <v-list-item
              v-for="option in currentMenuOptions"
              :key="option.key"
              :class="option.destructive ? 'text-red400' : 'text-neutral900'"
              @click="onMenuSelect(option)"
            >
              <template #prepend>
                <component :is="option.icon" class="text-h5 mr-3" />
              </template>
              <v-list-item-title class="font-weight-medium">
                {{ option.text }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </DropdownHybrid>
      </header>

      <!-- Sticky prev/next item navigation -->
      <template v-if="siblingNav">
        <BrandButton
          icon
          position="fixed"
          variant="basic"
          color="neutral900"
          border="0"
          size="large"
          class="sibling-nav sibling-nav--prev sibling-nav--transparent"
          :class="{ 'sibling-nav--mobile': display.smAndDown.value }"
          :disabled="!siblingNav.prevTo"
          :aria-label="t('cta.previous_item')"
          @click="goToSibling(siblingNav.prevTo)"
        >
          <PhCaretLeft class="text-h5" />
        </BrandButton>

        <BrandButton
          icon
          position="fixed"
          variant="basic"
          color="neutral900"
          border="0"
          size="large"
          class="sibling-nav sibling-nav--next sibling-nav--transparent"
          :class="{ 'sibling-nav--mobile': display.smAndDown.value }"
          :disabled="!siblingNav.nextTo"
          :aria-label="t('cta.next_item')"
          @click="goToSibling(siblingNav.nextTo)"
        >
          <PhCaretRight class="text-h5" />
        </BrandButton>
      </template>

      <v-container class="sheet-container py-0" :class="{ 'px-0': display.smAndDown.value }" :style="{ '--maxWidth': `${maxWidthDesktop}px` }">
        <v-card flat v-bind="subpageProps" class="subpage-content mt-md-6 mb-6">
          <router-view v-slot="{ Component }" class="route-page">
            <transition
              :name="transitionName"
              mode="out-in"
              @before-leave="onBeforeLeave"
              @after-leave="onAfterLeave"
              @after-enter="onAfterEnter"
            >
              <component
                :is="Component"
                ref="childComponent"
                @update:menu-options="onUpdateMenuOptions"
              />
            </transition>
          </router-view>
        </v-card>
      </v-container>
    </v-main>
  </div>
</template>

<script setup>
import { computed, ref, provide, watch, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import { PhArrowLeft, PhDotsThreeVertical, PhCaretLeft, PhCaretRight } from '@phosphor-icons/vue'
import { useRouteTransition } from '@/composables/useRouteTransition'
import DropdownHybrid from '@/components/global/DropdownHybrid.vue'
import BrandButton from '@/components/global/brand/BrandButton.vue'

// Minimum horizontal travel before a touch gesture counts as a navigation swipe.
const SWIPE_MIN_DISTANCE_PX = 50

// Define props
const props = defineProps({
  backButton: {
    type: [Object, Boolean],
    default: true,
  },
  maxWidthDesktop: {
    type: Number,
    default: 720,
  },
  menuOptions: {
    type: Array,
    default: () => [],
  },
  menuTitle: {
    type: String,
    default: '',
  },
})

// Setup router and route
const router = useRouter()
const route = useRoute()

// Use route transition composable
const {
  transitionName,
  transitioning,
  onBeforeLeave,
  onAfterLeave,
  onAfterEnter,
} = useRouteTransition()

// Get Vuetify display
const display = useDisplay()

const { t } = useI18n()

// Menu state
const menuOpen = ref(false)
const childComponent = ref(null)
const dynamicMenuOptions = ref([])
const dynamicMenuTitle = ref('')

// Header color state for mobile (set by child components like NoteDetail)
const headerColor = ref(null)

// Prev/next sibling navigation, set by detail views via the provided channel.
// Shape: { prevTo, nextTo } — route targets (string/object) or null at the edges.
const siblingNav = ref(null)

const goToSibling = to => {
  if (to) router.push(to)
}

// Computed properties
const showBackButton = computed(() => !!props.backButton)

const currentMenuOptions = computed(() => {
  // Prioritize dynamic options from child component
  if (dynamicMenuOptions.value.length > 0) {
    return dynamicMenuOptions.value
  }
  return props.menuOptions
})

const currentMenuTitle = computed(() => {
  return dynamicMenuTitle.value || props.menuTitle
})

const hasMenuOptions = computed(() => currentMenuOptions.value && currentMenuOptions.value.length > 0)

const menuProps = computed(() => ({
  offset: [-12, 0],
  openOnClick: true,
  closeOnContentClick: true,
  locationStrategy: 'connected',
  location: 'bottom end',
  origin: 'top end',
  rounded: 'lg',
  minWidth: 220,
}))

const subpageProps = computed(() => {
  if (display.mdAndUp.value) {
    return {
      class: 'rounded-lg',
      outlined: true,
    }
  }

  return { tile: true }
})

// Methods
const onBackButtonClicked = () => {
  if (props.backButton?.to) {
    const to = props.backButton.to?.(route, {
      isExtension: false,
      isExtensionPopup: false,
      isDesktop: display.mdAndUp.value,
      isMobile: display.smAndDown.value,
    }) ?? props.backButton.to

    if (typeof router.backTo === 'function') {
      router.backTo(to)
    } else {
      router.push(to)
    }
  } else {
    router.back()
  }
}

const onMenuSelect = option => {
  menuOpen.value = false
  if (option.action) {
    option.action()
  }
}

const onUpdateMenuOptions = options => {
  dynamicMenuOptions.value = options || []
}

// Watch child component for menu options
watch(childComponent, newVal => {
  if (newVal?.menuOptions) {
    dynamicMenuOptions.value = newVal.menuOptions
  }
}, { immediate: true })

// Update menu options when child component's exposed menuOptions changes
const updateMenuFromChild = () => {
  if (childComponent.value?.menuOptions) {
    const options = childComponent.value.menuOptions
    // Handle computed ref
    dynamicMenuOptions.value = typeof options.value !== 'undefined' ? options.value : options
  }
}

// Poll for child component menu options (since we can't reliably watch exposed properties)
onMounted(() => {
  const interval = setInterval(() => {
    updateMenuFromChild()
  }, 500)

  // Clean up after 5 seconds
  setTimeout(() => clearInterval(interval), 5000)
})

// Keyboard ← / → navigation between sibling items (desktop). Ignored while the
// user is typing in a field so it never hijacks the comment box or inputs.
const isTypingTarget = el => {
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable
}

const onKeydown = event => {
  if (!siblingNav.value || event.metaKey || event.ctrlKey || event.altKey) return
  if (isTypingTarget(event.target)) return

  if (event.key === 'ArrowLeft' && siblingNav.value.prevTo) {
    goToSibling(siblingNav.value.prevTo)
  } else if (event.key === 'ArrowRight' && siblingNav.value.nextTo) {
    goToSibling(siblingNav.value.nextTo)
  }
}

// Mobile swipe gesture to navigate siblings.
// Swipe left -> next item, swipe right -> previous item.
let touchStartX = 0
let touchStartY = 0
let touchTracked = false

const onTouchStart = event => {
  // Multi-touch is a pinch/zoom, never a navigation swipe.
  touchTracked = event.touches.length === 1
  if (!touchTracked) return

  touchStartX = event.touches[0].clientX
  touchStartY = event.touches[0].clientY
}

const onTouchEnd = event => {
  if (!touchTracked || !display.smAndDown.value) return
  touchTracked = false

  const distanceX = touchStartX - event.changedTouches[0].clientX
  const distanceY = touchStartY - event.changedTouches[0].clientY

  if (Math.abs(distanceX) < SWIPE_MIN_DISTANCE_PX) return
  // A vertical scroll always drifts sideways a little — only act when the
  // gesture is dominantly horizontal, otherwise scrolling jumps the item.
  if (Math.abs(distanceX) <= Math.abs(distanceY)) return

  if (distanceX > 0 && siblingNav.value?.nextTo) {
    goToSibling(siblingNav.value.nextTo)
  } else if (distanceX < 0 && siblingNav.value?.prevTo) {
    goToSibling(siblingNav.value.prevTo)
  }
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
  document.addEventListener('touchstart', onTouchStart, false)
  document.addEventListener('touchend', onTouchEnd, false)
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown)
  document.removeEventListener('touchstart', onTouchStart, false)
  document.removeEventListener('touchend', onTouchEnd, false)
})

// Provide menu control to child components
provide('focusDetailMenu', {
  menuOpen,
  closeMenu: () => {
    menuOpen.value = false
  },
  setMenuOptions: options => {
    dynamicMenuOptions.value = options
  },
  setMenuTitle: title => {
    dynamicMenuTitle.value = title
  },
  setHeaderColor: color => {
    headerColor.value = color
  },
  setSiblingNav: nav => {
    siblingNav.value = nav
  },
})
</script>

<style lang="scss" scoped>
.subpage-content {
  border-color: rgb(var(--v-theme-neutral50)) !important;
  background-color: rgb(var(--v-theme-neutral0));
  // No padding - content handles its own padding
  padding: 0 !important;
}

.sheet-container {
  max-width: var(--maxWidth);
}

.mobile-header {
  max-width: var(--maxWidth);
  width: 100%;
  margin-left: auto;
  margin-right: auto;
}

// Sticky prev/next item buttons — fixed, vertically centered at the viewport edges.
.sibling-nav {
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;

  &--prev {
    left: 16px;
  }

  &--next {
    right: 16px;
  }

  // Mobile sits closer to the edge and may overlap content slightly.
  &--mobile.sibling-nav--prev {
    left: 4px;
  }

  &--mobile.sibling-nav--next {
    right: 4px;
  }

  // Transparent background so users know buttons are interactive, not part of content
  &--transparent {
    background-color: rgba(var(--v-theme-neutral0), 0.6) !important;

    &:hover:not(:disabled) {
      background-color: rgba(var(--v-theme-neutral0), 0.8) !important;
    }
  }
}
</style>
